import { useEffect, useState } from 'react';
import { useParams } from '../lib/routing';
import { HeaderContent, HEADER_TITLE_CLASS } from '../components/shell/app-header';
import { cn } from '@/lib/utils';
import {
  ITEM_KINDS,
  createItem,
  createTheme,
  deleteItem,
  deleteProgramme,
  deleteTheme,
  fetchProgrammeTree,
  materializeDocument,
  updateItem,
  updateProgramme,
  updateTheme,
  type ItemRow,
  type ProgrammeTree,
  type ThemeRow,
} from '../lib/curriculum';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { ConfirmDialog } from '../components/confirm-dialog';
import { useIsAdminViewer } from '../hooks/use-is-admin-viewer';

const inputClass = "py-[6px] px-2 text-[13px]";

type ConfirmTarget =
  | { kind: 'materialize'; docId: string }
  | { kind: 'delete-programme' }
  | { kind: 'delete-theme'; theme: ThemeRow }
  | { kind: 'delete-item'; item: ItemRow }
  | null;

/* Programme tree editor (EDU-66) */
export default function AdminProgramme() {
  const isAdminViewer = useIsAdminViewer();
  const { id = '' } = useParams();
  const [tree, setTree] = useState<ProgrammeTree | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState<ConfirmTarget>(null);

  const reload = async () => {
    setError(null);
    try { setTree(await fetchProgrammeTree(id)); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };
  /* eslint-disable-next-line react-hooks/exhaustive-deps */
  useEffect(() => { void reload(); }, [id]);

  const guard = async (fn: () => Promise<void>) => {
    setBusy(true); setError(null);
    try { await fn(); await reload(); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };

  if (!tree) {
    return (
      <>
        <HeaderContent width="lg" showBurger backTo="/admin/curriculum" backLabel="Programmes">{null}</HeaderContent>
        <main className="max-w-[1000px] mx-auto px-4 pb-12 pt-4">
          {error ? <Alert>{error}</Alert> : <p className="text-muted-foreground">Chargement…</p>}
        </main>
      </>
    );
  }
  const p = tree.programme;

  const runConfirm = async () => {
    if (!confirmTarget) return;
    const target = confirmTarget;
    setConfirmTarget(null);
    if (target.kind === 'materialize') {
      await guard(async () => { await materializeDocument(target.docId); location.href = '/admin/curriculum'; });
    } else if (target.kind === 'delete-programme') {
      await guard(async () => { await deleteProgramme(p.id); location.href = '/admin/curriculum'; });
    } else if (target.kind === 'delete-theme') {
      await guard(() => deleteTheme(target.theme.id));
    } else if (target.kind === 'delete-item') {
      await guard(() => deleteItem(target.item.id));
    }
  };

  const confirmDialogProps = (() => {
    if (!confirmTarget) return null;
    switch (confirmTarget.kind) {
      case 'materialize':
        return {
          title: 'Re-matérialiser ce programme ?',
          description: 'Recrée les programmes de ce document depuis les extractions. Les modifications manuelles faites depuis sont perdues.',
          confirmLabel: 'Re-matérialiser',
          busyLabel: 'Re-matérialisation…',
          destructive: false,
        };
      case 'delete-programme':
        return {
          title: 'Supprimer ce programme ?',
          description: `« ${p.subject ?? 'Ce programme'} » et tout son contenu (thèmes, items) seront supprimés définitivement.`,
          confirmLabel: 'Supprimer',
          busyLabel: 'Suppression…',
          destructive: true,
        };
      case 'delete-theme':
        return {
          title: 'Supprimer ce thème ?',
          description: `« ${confirmTarget.theme.title} » et ses items seront supprimés définitivement.`,
          confirmLabel: 'Supprimer',
          busyLabel: 'Suppression…',
          destructive: true,
        };
      case 'delete-item':
        return {
          title: 'Supprimer cet item ?',
          description: 'Cet item sera supprimé définitivement.',
          confirmLabel: 'Supprimer',
          busyLabel: 'Suppression…',
          destructive: true,
        };
    }
  })();
  const itemCount = tree.themes.reduce((n, t) => n + t.items.length, 0);
  const disabled = busy || !isAdminViewer;

  return (
    <>
      <HeaderContent width="lg" showBurger backTo="/admin/curriculum" backLabel="Programmes">
        <div className="min-w-0">
          <h1 className={HEADER_TITLE_CLASS}>{p.subject ?? '— matière ?'}</h1>
          <p className="m-0 mt-0.5 text-[13px] sm:text-[13.5px] leading-snug text-muted-foreground">
            {[p.cycle, p.voie].filter(Boolean).join(' · ') || '—'} · {tree.themes.length} thèmes · {itemCount} items
          </p>
        </div>
      </HeaderContent>
      <main className="max-w-[1000px] mx-auto px-4 pb-12 pt-4">
        {error && <Alert>{error}</Alert>}

        <section className="bg-card border border-border rounded-[12px] p-3 mb-[14px]">
          <div className="flex justify-between gap-[10px] flex-wrap items-flex-start">
            <div className="min-w-0">
              <div className="text-[11px] text-muted-foreground">📄 {p.doc_title}</div>
            </div>
            <div className="flex gap-[6px] flex-wrap">
              <Button disabled={disabled}
                title="Programme actuellement en vigueur ? (le tuteur ne cite que les programmes en vigueur)"
                onClick={() => void guard(() => updateProgramme(p.id, { is_authoritative: !p.is_authoritative }))}
                className={p.is_authoritative ? "bg-naia-green text-card" : "bg-naia-info-bg text-foreground border-none"}>
                {p.is_authoritative ? '✓ En vigueur' : '✗ Supersédé'}
              </Button>
              {isAdminViewer && p.source_document_id && (
                <Button disabled={disabled} title="Reconstruit les programmes de ce document depuis les extractions"
                  onClick={() => setConfirmTarget({ kind: 'materialize', docId: p.source_document_id! })}
                  className="bg-naia-green text-card">🔄 Re-matérialiser</Button>
              )}
              {isAdminViewer && (
                <Button disabled={disabled}
                  onClick={() => setConfirmTarget({ kind: 'delete-programme' })}
                  className="bg-naia-error text-card">Supprimer</Button>
              )}
            </div>
          </div>
          <div className="flex gap-2 mt-[10px] flex-wrap">
            <label className="flex flex-col gap-0.5 text-[10px] font-extrabold tracking-[0.5px] text-muted-foreground">Statut
              <NativeSelect value={p.status} disabled={disabled} onChange={(e) => void guard(() => updateProgramme(p.id, { status: e.target.value }))} className={inputClass}>
                <option value="draft">draft</option><option value="published">published</option>
              </NativeSelect>
            </label>
            <label className="flex flex-col gap-0.5 text-[10px] font-extrabold tracking-[0.5px] text-muted-foreground">Voie
              <NativeSelect value={p.voie ?? ''} disabled={disabled} onChange={(e) => void guard(() => updateProgramme(p.id, { voie: e.target.value || null }))} className={inputClass}>
                <option value="">—</option>{['generale', 'technologique', 'professionnelle', 'commun'].map((v) => <option key={v} value={v}>{v}</option>)}
              </NativeSelect>
            </label>
            <label className="flex flex-col gap-0.5 text-[10px] font-extrabold tracking-[0.5px] text-muted-foreground flex-1 min-w-[160px]">Réf. BO
              <Input defaultValue={p.bo_reference ?? ''} disabled={disabled} onBlur={(e) => { if (e.target.value !== (p.bo_reference ?? '')) void guard(() => updateProgramme(p.id, { bo_reference: e.target.value || null })); }} className={cn(inputClass, "w-full")} />
            </label>
          </div>
        </section>

        {tree.themes.map((t) => (
          <section key={t.id} className="bg-card border border-border rounded-[12px] p-3 mb-[10px]">
            <div className="flex gap-2 items-flex-end flex-wrap">
              <Field label="Titre du thème" hint="partie / domaine / compétence du programme" className="flex-1 min-w-[160px]">
                <Input defaultValue={t.title} disabled={disabled} onBlur={(e) => { if (e.target.value.trim() && e.target.value !== t.title) void guard(() => updateTheme(t.id, { title: e.target.value.trim() })); }} className={cn(inputClass, "font-extrabold w-full")} />
              </Field>
              <Field label="Code" hint="libellé local d'origine (ex. « I.2 »), non unique">
                <Input defaultValue={t.code ?? ''} disabled={disabled} placeholder="—" onBlur={(e) => { if (e.target.value !== (t.code ?? '')) void guard(() => updateTheme(t.id, { code: e.target.value || null })); }} className={cn(inputClass, "w-[90px]")} />
              </Field>
              <Field label="Pages" hint="pages source (provenance, lecture seule)">
                <span className="py-[6px] px-2 text-[12px] border border-dashed border-border rounded-[8px] bg-secondary text-muted-foreground font-mono inline-block min-w-[40px] text-center">
                  {t.pages?.length ? t.pages.join(', ') : '—'}
                </span>
              </Field>
              <span className="text-[11px] text-muted-foreground whitespace-nowrap pb-[7px]">{t.items.length} items</span>
              {isAdminViewer && (
                <Button type="button" variant="ghost" size="icon" disabled={disabled} title="Supprimer le thème" onClick={() => setConfirmTarget({ kind: 'delete-theme', theme: t })} className="w-6 h-6 min-h-0 text-naia-error">×</Button>
              )}
            </div>
            <Field label="Intro" hint="cadrage / préambule du thème (non prescriptif, pas un item)" className="mt-[6px]">
              <Textarea defaultValue={t.intro ?? ''} disabled={disabled} rows={2} placeholder="—" onBlur={(e) => { if (e.target.value !== (t.intro ?? '')) void guard(() => updateTheme(t.id, { intro: e.target.value || null })); }} className="py-[6px] px-2 text-[13px] bg-secondary" />
            </Field>
            <div className="mt-[10px] overflow-x-auto">
              <ItemsHeader />
              <div className="grid gap-[6px] min-w-[600px]">
                {t.items.map((it) => (
                  <ItemEditor key={it.id} it={it} disabled={disabled} canMutate={isAdminViewer} guard={guard} onRequestDelete={() => setConfirmTarget({ kind: 'delete-item', item: it })} />
                ))}
              </div>
            </div>
            {isAdminViewer && (
              <Button variant="outline" disabled={disabled} onClick={() => void guard(() => createItem(p.id, t.id, 'connaissance', 'Nouvel item — à éditer'))} className="mt-2 text-[12px]">+ item</Button>
            )}
          </section>
        ))}

        {isAdminViewer && (
          <Button disabled={disabled} onClick={() => void guard(() => createTheme(p.id, 'Nouveau thème'))} className="bg-naia-blue text-card">+ Ajouter un thème</Button>
        )}
      </main>

      {confirmDialogProps && (
        <ConfirmDialog
          open
          title={confirmDialogProps.title}
          description={confirmDialogProps.description}
          confirmLabel={busy ? confirmDialogProps.busyLabel : confirmDialogProps.confirmLabel}
          destructive={confirmDialogProps.destructive}
          busy={busy}
          onConfirm={() => void runConfirm()}
          onCancel={() => setConfirmTarget(null)}
        />
      )}
    </>
  );
}

function ItemsHeader() {
  const cols: Array<[string, string]> = [
    ['TYPE', "nature de l'item (kind)"],
    ['CONTENU', 'texte prescriptif extrait'],
    ['NIVEAUX', 'classes visées (ex. cm1, cm2)'],
    ['LIBELLÉ SOURCE', "colonne/label d'origine dans le doc"],
    ['PAGES', 'pages source'],
    ['', ''],
  ];
  return (
    <div className="grid grid-cols-[120px_1fr_96px_130px_50px_26px] gap-[6px] min-w-[600px] pb-[3px] text-[9px] font-black tracking-[0.5px] text-muted-foreground uppercase">
      {cols.map(([l, h], i) => <span key={i} title={h}>{l}</span>)}
    </div>
  );
}

function ItemEditor({ it, disabled, canMutate, guard, onRequestDelete }: { it: ItemRow; disabled: boolean; canMutate: boolean; guard: (fn: () => Promise<void>) => Promise<void>; onRequestDelete: () => void }) {
  const levels = (it.progression?.levels ?? []).join(', ');
  return (
    <div className="grid grid-cols-[120px_1fr_96px_130px_50px_26px] gap-[6px] items-flex-start border-t border-naia-line-soft pt-[6px] min-w-[600px]">
      <NativeSelect defaultValue={it.kind} disabled={disabled} title="Type d'item" onChange={(e) => void guard(() => updateItem(it.id, { kind: e.target.value }))} className={inputClass}>
        {ITEM_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
      </NativeSelect>
      <Textarea defaultValue={it.content} disabled={disabled} rows={2} title="Contenu"
        onBlur={(e) => { if (e.target.value.trim() && e.target.value !== it.content) void guard(() => updateItem(it.id, { content: e.target.value.trim() })); }}
        className="py-[6px] px-2 text-[13px] bg-secondary resize-y" />
      <Input defaultValue={levels} disabled={disabled} placeholder="—" title="Niveaux : codes classe séparés par virgule"
        onBlur={(e) => { const lv = e.target.value.split(',').map((s) => s.trim()).filter(Boolean); if (lv.join(',') !== levels) void guard(() => updateItem(it.id, { progression: lv.length ? { kind: 'level', levels: lv } : { kind: 'cycle' } })); }}
        className={inputClass} />
      <Input defaultValue={it.source_label ?? ''} disabled={disabled} placeholder="—" title="Libellé/colonne d'origine"
        onBlur={(e) => { if (e.target.value !== (it.source_label ?? '')) void guard(() => updateItem(it.id, { source_label: e.target.value || null })); }}
        className={inputClass} />
      <span className="py-[6px] px-2 text-[12px] border border-dashed border-border rounded-[8px] bg-secondary text-muted-foreground font-mono inline-block min-w-[40px] text-center self-center" title="Pages source">
        {it.pages?.length ? it.pages.join(',') : '—'}
      </span>
      {canMutate && (
        <Button type="button" variant="ghost" size="icon" disabled={disabled} title="Supprimer l'item" onClick={onRequestDelete} className="w-6 h-6 min-h-0 text-naia-error">×</Button>
      )}
    </div>
  );
}

function Field({ label, hint, className, children }: { label: string; hint?: string; className?: string; children: React.ReactNode }) {
  return (
    <label className={cn("flex flex-col gap-0.5", className)}>
      <span title={hint} className="text-[9px] font-black tracking-[0.5px] text-muted-foreground uppercase">{label}{hint ? ' ⓘ' : ''}</span>
      {children}
    </label>
  );
}
